import { useState } from "react";
import { Button } from "./Button";

/**
 * The paging bar and the paging maths, shared by every history (#566).
 *
 * DataTable pages its rows with this; PagedList below pages anything that is a
 * plain list rather than a table (the Team Activity Feed, the Queen tab), so
 * the two read and behave the same: "Page 1 of 3 (120 rows)", Previous / Next
 * and a jump-to-page box. Nothing shows until a list passes one page.
 */
export function usePaged(items, pageSize = 50) {
  const [requested, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  // Clamped on read rather than reset in an effect: a filter that shrinks
  // the list must not leave the reader on a page that no longer exists.
  const page = Math.min(Math.max(requested, 1), pages);
  const shown = pages > 1 ? items.slice((page - 1) * pageSize, page * pageSize) : items;
  const goTo = (n) => setPage(Math.min(Math.max(Number(n) || 1, 1), pages));
  return { page, pages, shown, goTo, total: items.length };
}

export function PageBar({ page, pages, total, goTo, noun = "rows" }) {
  const [jump, setJump] = useState("");
  if (pages <= 1) return null;
  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "var(--space-3)", flexWrap: "wrap",
        marginTop: "var(--space-3)", paddingTop: "var(--space-3)",
        borderTop: "1px solid var(--color-border)",
      }}
    >
      <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        Page {page} of {pages} ({total} {noun})
      </span>
      <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
        <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => goTo(page - 1)}>
          Previous
        </Button>
        <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => goTo(page + 1)}>
          Next
        </Button>
        <form noValidate
          style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}
          onSubmit={(e) => { e.preventDefault(); goTo(jump); setJump(""); }}
        >
          <input
            type="number" min="1" max={pages} value={jump} placeholder="Page"
            aria-label="Go to page" name="go-to-page"
            onChange={(e) => setJump(e.target.value)}
            style={{ width: "5rem" }}
          />
          <Button size="sm" variant="secondary" type="submit">Go</Button>
        </form>
      </div>
    </div>
  );
}

/**
 * A plain list that pages itself once it holds more than `pageSize` items.
 * `renderItem(item)` returns the <li>; the caller keeps its own markup.
 */
export function PagedList({ items, renderItem, pageSize = 25, noun = "entries", listStyle }) {
  const paged = usePaged(items, pageSize);
  return (
    <>
      <ul style={listStyle}>{paged.shown.map(renderItem)}</ul>
      <PageBar {...paged} noun={noun} />
    </>
  );
}
